import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, GraduationCap, TrendingUp, Users, Award, Shield, ArrowRight, CheckCircle2 } from 'lucide-react';

const ForAdvisorsPage = () => {
  const benefits = [
    { icon: Briefcase, title: "Flexible Career Path", description: "Build your practice on your schedule, full-time or part-time" },
    { icon: TrendingUp, title: "Entrepreneurial Opportunity", description: "Unlimited income potential based on your effort and results" },
    { icon: Shield, title: "Access to Leading Carriers", description: "Represent 13+ A+ rated insurance companies" },
    { icon: GraduationCap, title: "Training & Mentorship", description: "Comprehensive education and ongoing support" },
    { icon: Award, title: "Technology Platform", description: "Modern quoting and CRM tools at your fingertips" },
    { icon: Users, title: "Leadership Path", description: "Build and lead your own team of advisors" }
  ];

  const steps = [
    { num: "01", title: "Apply Online", desc: "Submit your application in just 5 minutes" },
    { num: "02", title: "Get Licensed", desc: "We guide you through obtaining your state license" },
    { num: "03", title: "Complete Training", desc: "Learn products, compliance, and sales strategies" },
    { num: "04", title: "Start Earning", desc: "Begin helping clients and building your income" }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="for-advisors-page">
      {/* Hero */}
      <section className="py-24 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-transparent to-[#020617]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
              Career Opportunity
            </span>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Build a Career in Financial Protection
            </h1>
            <p className="text-slate-300 text-xl mb-8">
              ShopCompareInsure is building a national network of insurance advisors helping families access life insurance and wealth protection solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/become-an-agent"
                className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
              >
                Apply Now
              </Link>
              <Link
                to="/business-model"
                className="inline-flex items-center justify-center border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10 px-8 py-4 uppercase tracking-wider text-sm transition-all"
              >
                Learn the Business Model
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl text-white mb-4">Why Become an Advisor?</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Join thousands of successful advisors who have built rewarding careers helping families.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="bg-slate-900/50 border border-slate-800 p-6 hover:border-[#C5A059]/30 transition-colors">
                  <div className="w-12 h-12 rounded-lg bg-[#C5A059]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-slate-400 text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl text-white mb-4">Getting Started is Simple</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="text-5xl font-serif text-[#C5A059]/30 mb-4">{step.num}</div>
                <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                <p className="text-slate-400 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Business Model", path: "/business-model", desc: "How our advisor platform works" },
              { title: "Training & Licensing", path: "/training-licensing", desc: "Education and licensing support" },
              { title: "Compensation Model", path: "/compensation-model", desc: "How advisors earn income" },
              { title: "Success Stories", path: "/success-stories", desc: "Real advisor achievements" }
            ].map((link, idx) => (
              <Link
                key={idx}
                to={link.path}
                className="group bg-slate-900/50 border border-slate-800 p-6 hover:border-[#C5A059]/30 transition-all"
              >
                <h3 className="text-white font-semibold mb-2 group-hover:text-[#C5A059] transition-colors">{link.title}</h3>
                <p className="text-slate-500 text-sm mb-4">{link.desc}</p>
                <span className="text-[#C5A059] text-sm flex items-center">
                  Learn More <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Ready to Start Your Journey?</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Join our growing network of successful advisors helping families achieve financial security.
          </p>
          <Link
            to="/become-an-agent"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Apply Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ForAdvisorsPage;
